import { Hub } from 'aws-amplify/utils'
import { sessionExpired } from 'common/actions/auth'
import { isSessionEndedError, readSessionTokens } from 'utils/session'
import {
  getSessionDeadline,
  isPastDeadline,
  SESSION_CHANNEL,
  SESSION_LIMIT_REACHED,
  SESSION_MAX_AGE,
  SESSION_REJECTED
} from 'utils/sessionLimit'

/**
 * Sends the user to the login page when the session ends.
 *
 * The session ends in three ways, and all of them dispatch sessionExpired:
 *
 * - Amplify cannot renew the token any more, for example because the refresh
 *   token expired or was revoked. It emits tokenRefresh_failure.
 * - The session reaches SESSION_MAX_AGE after the sign in. A timer set for that
 *   moment ends it, so a tab left open stops polling. Browsers can run timers
 *   late in background tabs, so we also check when the tab is shown again, and
 *   request.js checks before each request.
 * - The API rejects a request with a 401 or 403, and the polling sagas report
 *   it with reportSessionRejected. The page load reports it the same way when
 *   it cannot load the user.
 *
 * Amplify shares one renewal between parallel requests, but emits the failure
 * once for each of them. Several requests can find the session over the limit,
 * and a poll can get a 401 for the same session. So we dispatch sessionExpired
 * only once, until the user signs in again.
 *
 * @param {Function} dispatch - The Redux store dispatch.
 * @returns {Function} Stops listening.
 */
export function listenForEndedSession(dispatch) {
  let sessionEnded = false
  let deadline
  let timer
  let sessionRead = 0

  const endSession = () => {
    if (sessionEnded) return
    sessionEnded = true
    clearTimeout(timer)
    dispatch(sessionExpired())
  }

  // Sets the timer for the deadline of the current session. With no session
  // there is nothing to end. A failed read, such as a network error, leaves the
  // timer unset: the next successful renewal sets it, and until then request.js
  // still checks before each request.
  const scheduleDeadline = async () => {
    const currentRead = ++sessionRead
    clearTimeout(timer)
    let nextDeadline
    try {
      nextDeadline = getSessionDeadline(await readSessionTokens())
    } catch (err) {
      return
    }
    // A sign out, a newer read or disposal can happen while tokens are read.
    if (currentRead !== sessionRead) return
    // Only a new sign in, for example in another tab, has a deadline in the
    // future. A session that already ended here never does, so a late read of
    // it must not let it end a second time.
    if (nextDeadline !== undefined && !isPastDeadline(nextDeadline)) sessionEnded = false
    deadline = nextDeadline
    if (deadline === undefined) return

    if (isPastDeadline(deadline)) {
      endSession()
      return
    }

    // Another tab can replace the shared tokens without emitting signedIn
    // here. Read them again before enforcing this session's deadline.
    // A deadline is never more than SESSION_MAX_AGE away, unless the clock of
    // the computer is wrong. A delay over about 24.8 days makes setTimeout fire
    // at once, which would read the session again in a tight loop, so we cap
    // it. The timer then reads the session again and waits once more.
    timer = setTimeout(scheduleDeadline, Math.min(deadline - Date.now(), SESSION_MAX_AGE))
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && isPastDeadline(deadline)) scheduleDeadline()
  }

  const stopAuth = Hub.listen('auth', ({ payload }) => {
    if (payload.event === 'signedIn') {
      sessionEnded = false
      scheduleDeadline()
      return
    }
    if (payload.event === 'signedOut') {
      sessionRead++
      clearTimeout(timer)
      deadline = undefined
      return
    }
    if (payload.event === 'tokenRefresh') {
      if (deadline === undefined) scheduleDeadline()
      return
    }
    if (payload.event !== 'tokenRefresh_failure') return

    // Other errors, such as a network error, also fail the renewal, but Amplify
    // keeps the session then, so we leave the user where they are.
    if (isSessionEndedError(payload.data?.error)) endSession()
  })

  const stopSession = Hub.listen(SESSION_CHANNEL, ({ payload }) => {
    if (payload.event === SESSION_LIMIT_REACHED || payload.event === SESSION_REJECTED) endSession()
  })

  document.addEventListener('visibilitychange', onVisibilityChange)
  scheduleDeadline()

  return () => {
    sessionRead++
    stopAuth()
    stopSession()
    document.removeEventListener('visibilitychange', onVisibilityChange)
    clearTimeout(timer)
  }
}
